import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import {
  Mail,
  Phone,
  Clock,
  Send,
  MessageSquare,
  CheckCircle,
  Loader2,
  Calendar,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        message: formData.message,
      });
      toast.success(response.data.message);
      setFormData({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Call Us",
      value: "(949) 354-1412",
      href: "tel:+19493541412",
      description: "Mon-Fri 10am-4pm PST",
    },
    {
      icon: Mail,
      title: "Email Us",
      value: "info@5starmarketing.com",
      href: "mailto:info@5starmarketing.com",
      description: "We respond within 24 hours",
    },
    {
      icon: Clock,
      title: "Business Hours",
      value: "10am - 4pm PST",
      href: "#",
      description: "Monday through Friday",
    },
  ];

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero */}
      <section className="hero-gradient py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#F37021]/10 text-[#F37021] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <MessageSquare className="h-4 w-4" />
              Contact Us
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Get Your <span className="gradient-text">Free Consultation</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Ready to protect your online reputation? Contact us for a free, confidential 
              consultation. We'll analyze your current situation and provide actionable recommendations.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 lg:py-28 section-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <h2
                className="text-2xl font-bold text-foreground"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Let's Talk
              </h2>
              <p className="text-muted-foreground">
                Whether you're dealing with negative reviews, want to improve your ratings, 
                or need a complete online marketing strategy, we're here to help.
              </p>

              <div className="space-y-4">
                {contactInfo.map((info, index) => (
                  <a
                    key={info.title}
                    href={info.href}
                    data-testid={`contact-info-${index}`}
                    className="flex items-center gap-4 p-4 bg-muted rounded-xl hover:bg-muted/70 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-lg bg-[#F37021]/10 flex items-center justify-center flex-shrink-0">
                      <info.icon className="h-6 w-6 text-[#F37021]" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{info.value}</p>
                      <p className="text-sm text-muted-foreground">{info.description}</p>
                    </div>
                  </a>
                ))}
              </div>

              {/* What to Expect */}
              <Card className="bg-[#F37021]/10 border-[#F37021]/20 mt-8">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-foreground mb-4">What to expect:</h3>
                  <ul className="space-y-3">
                    {[
                      "Response within 24 hours",
                      "Free reputation analysis",
                      "Custom strategy recommendations",
                      "Transparent pricing",
                      "No obligation to purchase",
                    ].map((item, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-[#F37021]" />
                        <span className="text-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Book Consultation CTA */}
              <Link to="/book-consultation" className="block">
                <Card className="bg-card border-border hover:border-[#F37021]/50 transition-colors cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <Calendar className="h-8 w-8 text-[#F37021]" />
                      <div>
                        <p className="font-semibold text-foreground">Book a Consultation</p>
                        <p className="text-sm text-muted-foreground">Schedule a call on your calendar</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-card border-border">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-foreground mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Send Us a Message
                  </h3>
                  
                  <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          data-testid="contact-name-input"
                          placeholder="John Smith"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          data-testid="contact-email-input"
                          placeholder="john@company.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company">Company</Label>
                        <Input
                          id="company"
                          name="company"
                          data-testid="contact-company-input"
                          placeholder="Your Company"
                          value={formData.company}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          data-testid="contact-phone-input"
                          placeholder="(949) 354-1412"
                          value={formData.phone}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">How can we help? *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        data-testid="contact-message-input"
                        placeholder="Tell us about your reputation challenges or goals..."
                        rows={6}
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      data-testid="contact-submit-btn"
                      disabled={isLoading}
                      className="w-full bg-[#F37021] hover:bg-[#D95F18] text-white rounded-full py-6 text-lg font-semibold"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>

                  <p className="text-xs text-muted-foreground text-center mt-6">
                    Your information is confidential and will never be shared.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="py-20 lg:py-28 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2
            className="text-3xl sm:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Platforms We Manage
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-12">
            We monitor and manage your reputation across all major review platforms.
          </p>
          <div className="flex flex-wrap justify-center gap-6 lg:gap-10">
            {["Google", "Yelp", "Avvo", "Vitals", "WebMD", "Zillow", "Facebook", "BBB"].map((platform) => (
              <div
                key={platform}
                className="bg-white/5 rounded-lg px-6 py-3 text-white font-medium hover:bg-[#F37021]/20 transition-colors"
              >
                {platform}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
